import React from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Loader2 } from 'lucide-react';

import { Button } from './button';
import { getOtherOrganization } from '../../lib/apiError';
import { useImpersonation, useIsSuperAdmin } from '../../stores/authStore';
import { useSwitchOrganization } from '../../hooks/useSwitchOrganization';

interface NotFoundStateProps {
  /** The error the detail page got while loading its object. */
  error: unknown;
  /** The page's usual not-found / error content, shown in every other case. */
  children: React.ReactNode;
}

/**
 * Detail-page 404. When a super admin opens an object of another organization,
 * the backend names that org: offer to switch into it, which remounts the page
 * on the same URL. Everyone else gets the page's usual content.
 */
export const NotFoundState: React.FC<NotFoundStateProps> = ({ error, children }) => {
  const { t } = useTranslation();
  const isSuperAdmin = useIsSuperAdmin();
  const { isImpersonating } = useImpersonation();
  const { switchTo, switching } = useSwitchOrganization();
  const org = getOtherOrganization(error);

  if (!org || !isSuperAdmin || isImpersonating) return <>{children}</>;

  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-4 rounded-xl border border-border bg-card px-6 py-10 text-center">
      <Building2 className="h-8 w-8 text-muted-foreground" aria-hidden />
      <p className="text-sm text-foreground">{t('notFound.otherOrganization', { name: org.name })}</p>
      <Button onClick={() => void switchTo(org.id, { stayOnPage: true })} disabled={switching}>
        {switching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {t('notFound.switchToOrg', { name: org.name })}
      </Button>
    </div>
  );
};

export default NotFoundState;
